import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { api, errMessage } from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { useLocationCtx } from "@/context/LocationContext";
import { toast } from "sonner";
import { Ticket, Loader2, ArrowLeft, Camera, Store, User, MapPin } from "lucide-react";
import { COUNTRIES, BIRTH_YEARS, GENDER_OPTIONS } from "@/lib/countries";
import LegalConsentCheckbox from "@/components/LegalConsentCheckbox";
import InterestsPicker from "@/components/InterestsPicker";

export default function ClientAuthDialog({ open, onOpenChange, onSuccess }) {
  const { clientLoginToken } = useAuth();
  const { loc } = useLocationCtx();
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose"); // choose | login | register | forgot | reset
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", city: "", country: "Italia", birth_date: "", gender: "", code: "", new_password: "" });
  const [photo, setPhoto] = useState("");
  const [interests, setInterests] = useState([]);
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [pendingCity, setPendingCity] = useState("");

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.get(`/geo/reverse?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (r.data?.city) setPendingCity(r.data.city);
          else toast.error("Non siamo riusciti a rilevare la città. Inseriscila a mano.");
        } catch (_) { toast.error("Non siamo riusciti a rilevare la città."); }
        finally { setGeoLoading(false); }
      },
      () => { setGeoLoading(false); toast.error("Permesso posizione negato."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  useEffect(() => {
    if (open) {
      setMode("choose");
      setError(""); setInfo(""); setAccepted(false);
      if (macros.length === 0) {
        api.get("/categories").then((r) => setMacros(r.data.categories || r.data || [])).catch(() => {});
      }
    }
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const go = (m) => { setMode(m); setError(""); setInfo(""); };
  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto(await fileToDataUrl(f, 400));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");

    if (mode === "forgot") {
      if (!form.email) return setError("Inserisci la tua email.");
      setLoading(true);
      try {
        await api.post("/client/auth/forgot-password", { email: form.email });
        setInfo("Se l'email è registrata, riceverai un codice a 6 cifre. Controlla la posta.");
        setMode("reset");
      } catch (err) { setError(errMessage(err)); }
      finally { setLoading(false); }
      return;
    }

    if (mode === "reset") {
      if (!form.code || !form.new_password) return setError("Inserisci codice e nuova password.");
      setLoading(true);
      try {
        const r = await api.post("/client/auth/reset-password", { email: form.email.trim().toLowerCase(), code: form.code.trim(), new_password: form.new_password.trim() });
        clientLoginToken(r.data);
        toast.success("Password reimpostata!");
        onOpenChange(false);
        onSuccess && onSuccess(r.data);
      } catch (err) { setError(errMessage(err)); }
      finally { setLoading(false); }
      return;
    }

    if (!form.email || !form.password) return setError("Inserisci email e password.");
    if (mode === "register" && !form.name) return setError("Inserisci il tuo nome.");
    if (mode === "register" && !form.phone.trim()) return setError("Inserisci il numero di telefono.");
    if (mode === "register" && !form.city.trim()) return setError("Inserisci la tua città.");
    if (mode === "register" && !form.country) return setError("Seleziona la tua nazione.");
    if (mode === "register" && !form.birth_date) return setError("Inserisci la tua data di nascita.");
    if (mode === "register" && !form.gender) return setError("Seleziona il tuo genere.");
    if (mode === "register" && !accepted) return setError("Per registrarti devi accettare i Termini e Condizioni e la Privacy Policy.");
    setLoading(true);
    try {
      const path = mode === "register" ? "/client/auth/register" : "/client/auth/login";
      const payload = mode === "register"
        ? { email: form.email.trim().toLowerCase(), password: form.password.trim(), name: form.name, phone: form.phone, city: form.city, country: form.country, birth_date: form.birth_date, gender: form.gender, photo, lat: loc.lat, lng: loc.lng, radius: loc.radius, accepted_terms: true }
        : { email: form.email.trim().toLowerCase(), password: form.password.trim() };
      const r = await api.post(path, payload);
      clientLoginToken(r.data);
      if (mode === "register" && interests.length) {
        try { await api.put("/client/auth/profile", { interests }, { headers: { Authorization: `Bearer ${r.data.token}` } }); } catch (_) {}
      }
      toast.success(mode === "register" ? "Benvenuto su KoupoNow!" : "Bentornato!");
      onOpenChange(false);
      onSuccess && onSuccess(r.data);
    } catch (err) {
      if (err?.response?.status === 409) { setError("Email già registrata. Passa ad Accedi."); setMode("login"); }
      else setError(errMessage(err));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    if (mode === "register" && !accepted) { setError("Per registrarti devi accettare i Termini e Condizioni e la Privacy Policy."); return; }
    localStorage.setItem("kn_oauth_role", "client");
    localStorage.setItem("kn_oauth_return", window.location.pathname);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = { choose: "Accedi a KoupoNow", register: "Crea il tuo account", login: "Bentornato", forgot: "Recupera password", reset: "Nuova password" };

  const goVendor = () => { onOpenChange(false); navigate("/vendor"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl p-0 max-h-[90dvh] overflow-y-auto" data-testid="client-auth-dialog">
        <div className="kn-gradient px-6 pt-7 pb-8 text-white">
          <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center mb-3"><Ticket className="h-6 w-6" /></div>
          <DialogHeader><DialogTitle className="text-white text-2xl font-extrabold">{titles[mode]}</DialogTitle></DialogHeader>
          <DialogDescription className="text-white/85 text-sm mt-1">
            {mode === "choose" ? "Come vuoi entrare?"
              : mode === "forgot" ? "Ti inviamo un codice via email."
              : mode === "reset" ? "Inserisci il codice ricevuto e la nuova password."
              : "Salva le offerte, richiedi i coupon e traccia i tuoi risparmi."}
          </DialogDescription>
        </div>

        {mode === "choose" ? (
          <div className="px-6 py-6 space-y-3" data-testid="role-choice">
            <button type="button" data-testid="role-buyer" onClick={() => go("login")}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-slate-200 hover:border-[#E63946] hover:bg-[#FFF1F2] transition-all text-left">
              <div className="h-12 w-12 rounded-xl bg-[#FFE4E6] flex items-center justify-center shrink-0"><User className="h-6 w-6 text-[#E63946]" /></div>
              <div>
                <p className="font-extrabold text-slate-900">Sono un acquirente</p>
                <p className="text-xs text-slate-500">Cerca offerte e coupon nella tua zona</p>
              </div>
            </button>
            <button type="button" data-testid="role-vendor" onClick={goVendor}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all text-left">
              <div className="h-12 w-12 rounded-xl bg-slate-900 flex items-center justify-center shrink-0"><Store className="h-6 w-6 text-white" /></div>
              <div>
                <p className="font-extrabold text-slate-900">Sono un venditore</p>
                <p className="text-xs text-slate-500">Gestisci il tuo negozio e le offerte</p>
              </div>
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="px-6 py-5 space-y-3.5">
          {(mode === "forgot" || mode === "reset" || mode === "login" || mode === "register") && mode !== "reset" && (
            <div className="space-y-1.5">
              <Label htmlFor="ca-email">Email</Label>
              <Input id="ca-email" data-testid="client-auth-email" type="email" value={form.email} onChange={set("email")} placeholder="tuamail@esempio.it" disabled={mode === "reset"} />
            </div>
          )}

          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="ca-name">Nome</Label>
              <Input id="ca-name" data-testid="client-auth-name" value={form.name} onChange={set("name")} placeholder="Mario Rossi" />
            </div>
          )}

          {mode === "register" && (
            <div className="flex items-center gap-3">
              <label data-testid="client-avatar-upload" className="h-16 w-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer shrink-0">
                {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : <Camera className="h-5 w-5 text-slate-400" />}
                <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
              </label>
              <span className="text-sm text-slate-500">Aggiungi una foto profilo (opzionale)</span>
            </div>
          )}

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ca-phone">Telefono *</Label>
                <Input id="ca-phone" data-testid="client-auth-phone" value={form.phone} onChange={set("phone")} placeholder="+39 333..." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ca-city">Città *</Label>
                <Input id="ca-city" data-testid="client-auth-city" value={form.city} onChange={set("city")} placeholder="Pesaro" />
              </div>
            </div>
          )}

          {mode === "register" && (
            <button type="button" onClick={useMyLocation} disabled={geoLoading} data-testid="client-auth-use-location"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E63946] disabled:opacity-60">
              {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
              Usa la mia posizione per la città
            </button>
          )}

          {mode === "register" && pendingCity && (
            <div data-testid="client-auth-city-confirm" className="rounded-xl border border-[#E63946]/20 bg-[#FFF1F2] p-3 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-700">Abbiamo rilevato <b>{pendingCity}</b>. È corretta?</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" data-testid="client-auth-city-confirm-yes" onClick={() => { setForm((f) => ({ ...f, city: pendingCity })); setPendingCity(""); }}
                  className="px-3 py-1 rounded-full bg-[#E63946] text-white text-sm font-bold">Conferma</button>
                <button type="button" data-testid="client-auth-city-confirm-no" onClick={() => setPendingCity("")}
                  className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-semibold">No, la scrivo io</button>
              </div>
            </div>
          )}

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ca-country">Nazione *</Label>
                <select id="ca-country" data-testid="client-auth-country" value={form.country} onChange={set("country")}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ca-birthdate">Data di nascita *</Label>
                <input type="date" id="ca-birthdate" data-testid="client-auth-birthdate" value={form.birth_date} onChange={set("birth_date")}
                  max={new Date().toISOString().slice(0, 10)} min="1900-01-01"
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30" />
              </div>
            </div>
          )}

          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="ca-gender">Genere *</Label>
              <select id="ca-gender" data-testid="client-auth-gender" value={form.gender} onChange={set("gender")}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
                <option value="">Seleziona…</option>
                {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          )}

          {mode === "register" && macros.length > 0 && (
            <div className="space-y-1.5">
              <Label>I tuoi interessi (per offerte su misura)</Label>
              <div data-testid="client-interests">
                <InterestsPicker macros={macros} value={interests} onChange={setInterests} prefix="interest" />
              </div>
            </div>
          )}

          {(mode === "login" || mode === "register") && (
            <div className="space-y-1.5">
              <Label htmlFor="ca-pass">Password</Label>
              <PasswordInput id="ca-pass" testId="client-auth-password" value={form.password} onChange={set("password")} placeholder="••••••••" />
              {mode === "login" && (
                <button type="button" data-testid="forgot-link" onClick={() => go("forgot")} className="text-xs text-[#E63946] font-semibold">Password dimenticata?</button>
              )}
            </div>
          )}

          {mode === "reset" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ca-code">Codice (6 cifre)</Label>
                <Input id="ca-code" data-testid="reset-code" value={form.code} onChange={set("code")} placeholder="123456" maxLength={6} className="text-center text-xl tracking-[0.3em] font-bold" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ca-newpass">Nuova password</Label>
                <PasswordInput id="ca-newpass" testId="reset-newpass" autoComplete="new-password" value={form.new_password} onChange={set("new_password")} placeholder="••••••••" />
              </div>
            </>
          )}

          {mode === "register" && (
            <div className="pt-0.5">
              <LegalConsentCheckbox checked={accepted} onChange={setAccepted} testId="client-legal-consent" />
            </div>
          )}

          {info && <p className="text-sm text-emerald-600 font-medium" data-testid="client-auth-info">{info}</p>}
          {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="client-auth-error">{error}</p>}

          <Button type="submit" disabled={loading} data-testid="client-auth-submit" className="w-full h-12 text-base font-bold rounded-xl">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" />
              : mode === "register" ? "Registrati"
              : mode === "login" ? "Accedi"
              : mode === "forgot" ? "Invia codice"
              : "Reimposta password"}
          </Button>

          {(mode === "login" || mode === "register") && (
            <>
              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">oppure</span><div className="h-px flex-1 bg-slate-200" />
              </div>
              <Button type="button" variant="outline" onClick={googleLogin} data-testid="client-auth-google" className="w-full h-12 rounded-xl font-semibold gap-2">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continua con Google
              </Button>
              {mode === "login" ? (
                <Button type="button" variant="outline" data-testid="client-auth-toggle" onClick={() => go("register")}
                  className="w-full h-12 rounded-xl font-bold text-[#E63946] border-[#E63946]/40 hover:bg-[#FFF1F2]">
                  Registrati
                </Button>
              ) : (
                <p className="text-center text-sm text-slate-500 pt-1">
                  Hai già un account?{" "}
                  <button type="button" data-testid="client-auth-toggle" onClick={() => go("login")} className="text-[#E63946] font-bold">Accedi</button>
                </p>
              )}
            </>
          )}

          {(mode === "forgot" || mode === "reset") && (
            <button type="button" onClick={() => go("login")} className="flex items-center justify-center gap-1.5 w-full text-sm text-slate-500 font-semibold pt-1">
              <ArrowLeft className="h-4 w-4" /> Torna al login
            </button>
          )}

          {(mode === "login" || mode === "register") && (
            <button type="button" data-testid="back-to-choose" onClick={() => go("choose")} className="flex items-center justify-center gap-1.5 w-full text-xs text-slate-400 font-semibold pt-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Cambia tipo di accesso
            </button>
          )}
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
